/**
 * vehicle/renderer.ts — Drawing Things That Move
 *
 * Vehicles are rendered as procedural pixel art, similar to furniture
 * in room/furniture.ts. Each vehicle type has its own drawing logic
 * using Canvas 2D primitives.
 */

import type { VehicleState } from "./system.js";
import { getVehicleTemplate } from "./system.js";

// ─── Vehicle Renderer ───────────────────────────────────────────────────────

export function renderVehicle(
  ctx: CanvasRenderingContext2D,
  vehicle: VehicleState,
  screenX: number, screenY: number,
  scale: number,
  facing: number
): void {
  const template = getVehicleTemplate(vehicle.templateId);
  if (!template) return;

  const s = scale;
  const f = facing;
  const paint = vehicle.customization["paint"]?.color ?? "#8a6a3a";

  switch (template.type) {
    case "cart":
      drawCart(ctx, screenX, screenY, s, f, paint);
      break;
    case "carriage":
      drawCarriage(ctx, screenX, screenY, s, f, paint);
      break;
    case "boat":
      drawBoat(ctx, screenX, screenY, s, f, paint);
      break;
    case "flying_carpet":
      drawFlyingCarpet(ctx, screenX, screenY, s, f, paint);
      break;
    case "bicycle":
      drawBicycle(ctx, screenX, screenY, s, f, paint);
      break;
  }
}

// ─── Cart ───────────────────────────────────────────────────────────────────

function drawCart(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  s: number, f: number,
  color: string
): void {
  // Bed
  ctx.fillStyle = color;
  ctx.fillRect(x - 10 * s, y - 4 * s, 20 * s, 6 * s);

  // Sides
  ctx.fillStyle = darken(color, 0.15);
  ctx.fillRect(x - 10 * s, y - 8 * s, 2 * s, 4 * s);
  ctx.fillRect(x + 8 * s, y - 8 * s, 2 * s, 4 * s);

  // Wheels with spokes
  drawWheel(ctx, x - 7 * s, y + 3 * s, 3 * s);
  drawWheel(ctx, x + 7 * s, y + 3 * s, 3 * s);

  // Axle
  ctx.strokeStyle = "#2a2a2a";
  ctx.lineWidth = 1 * s;
  ctx.beginPath();
  ctx.moveTo(x - 7 * s, y + 3 * s);
  ctx.lineTo(x + 7 * s, y + 3 * s);
  ctx.stroke();
}

// ─── Carriage ───────────────────────────────────────────────────────────────

function drawCarriage(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  s: number, f: number,
  color: string
): void {
  // Body
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x - 14 * s, y - 2 * s);
  ctx.lineTo(x - 12 * s, y - 12 * s);
  ctx.lineTo(x + 12 * s, y - 12 * s);
  ctx.lineTo(x + 14 * s, y - 2 * s);
  ctx.closePath();
  ctx.fill();

  // Roof
  ctx.fillStyle = darken(color, 0.2);
  ctx.fillRect(x - 13 * s, y - 14 * s, 26 * s, 3 * s);

  // Door
  ctx.fillStyle = darken(color, 0.3);
  ctx.fillRect(x - 3 * s, y - 10 * s, 6 * s, 7 * s);

  // Window
  ctx.fillStyle = "rgba(100,120,160,0.4)";
  ctx.fillRect(x - 2 * s, y - 10 * s, 4 * s, 3 * s);

  // Wheels (larger, with spokes)
  drawWheel(ctx, x - 10 * s, y + 2 * s, 4 * s);
  drawWheel(ctx, x + 10 * s, y + 2 * s, 4 * s);

  // Lantern hooks (warm glow)
  ctx.fillStyle = "rgba(255,180,60,0.3)";
  ctx.beginPath();
  ctx.arc(x - 13 * s, y - 8 * s, 2 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#c8a040";
  ctx.beginPath();
  ctx.arc(x - 13 * s, y - 8 * s, 0.8 * s, 0, Math.PI * 2);
  ctx.fill();
}

// ─── Boat ───────────────────────────────────────────────────────────────────

function drawBoat(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  s: number, f: number,
  color: string
): void {
  // Hull
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x - 12 * s, y);
  ctx.quadraticCurveTo(x - 14 * s, y + 4 * s, x, y + 6 * s);
  ctx.quadraticCurveTo(x + 14 * s, y + 4 * s, x + 12 * s, y);
  ctx.closePath();
  ctx.fill();

  // Rim
  ctx.strokeStyle = darken(color, 0.3);
  ctx.lineWidth = 1.5 * s;
  ctx.beginPath();
  ctx.moveTo(x - 12 * s, y);
  ctx.lineTo(x + 12 * s, y);
  ctx.stroke();

  // Seat
  ctx.fillStyle = darken(color, 0.15);
  ctx.fillRect(x - 4 * s, y - 2 * s, 8 * s, 2 * s);

  // Water ripples around hull
  ctx.strokeStyle = "rgba(100,140,180,0.2)";
  ctx.lineWidth = 0.5 * s;
  ctx.beginPath();
  ctx.ellipse(x, y + 7 * s, 14 * s, 2.5 * s, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = "rgba(100,140,180,0.12)";
  ctx.beginPath();
  ctx.ellipse(x, y + 8.5 * s, 16 * s, 3 * s, 0, 0, Math.PI * 2);
  ctx.stroke();
}

// ─── Flying Carpet ──────────────────────────────────────────────────────────

function drawFlyingCarpet(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  s: number, f: number,
  color: string
): void {
  // Carpet body (parallelogram for isometric feel)
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x - 10 * s, y - 2 * s);
  ctx.lineTo(x + 2 * s, y - 6 * s);
  ctx.lineTo(x + 10 * s, y - 2 * s);
  ctx.lineTo(x - 2 * s, y + 2 * s);
  ctx.closePath();
  ctx.fill();

  // Pattern (diamond in center)
  ctx.fillStyle = darken(color, 0.2);
  ctx.beginPath();
  ctx.moveTo(x, y - 4 * s);
  ctx.lineTo(x + 4 * s, y - 2 * s);
  ctx.lineTo(x, y);
  ctx.lineTo(x - 4 * s, y - 2 * s);
  ctx.closePath();
  ctx.fill();

  // Fringe
  ctx.strokeStyle = darken(color, 0.3);
  ctx.lineWidth = 0.5 * s;
  for (let i = -8; i <= 8; i += 2) {
    ctx.beginPath();
    ctx.moveTo(x + i * s, y + 2 * s);
    ctx.lineTo(x + i * s, y + 3.5 * s);
    ctx.stroke();
  }

  // Hover shadow
  ctx.fillStyle = "rgba(0,0,0,0.15)";
  ctx.beginPath();
  ctx.ellipse(x, y + 8 * s, 8 * s, 2 * s, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ─── Bicycle ────────────────────────────────────────────────────────────────

function drawBicycle(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  s: number, f: number,
  color: string
): void {
  // Wheels
  ctx.strokeStyle = "#3a3a3a";
  ctx.lineWidth = 1 * s;
  ctx.beginPath();
  ctx.arc(x - 5 * s * f, y + 2 * s, 3 * s, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x + 5 * s * f, y + 2 * s, 3 * s, 0, Math.PI * 2);
  ctx.stroke();

  // Frame
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2 * s;
  ctx.beginPath();
  ctx.moveTo(x - 5 * s * f, y + 2 * s);
  ctx.lineTo(x, y - 3 * s);
  ctx.lineTo(x + 5 * s * f, y + 2 * s);
  ctx.moveTo(x, y - 3 * s);
  ctx.lineTo(x - 2 * s * f, y + 2 * s);
  ctx.stroke();

  // Handlebars
  ctx.beginPath();
  ctx.moveTo(x + 4 * s * f, y - 4 * s);
  ctx.lineTo(x + 6 * s * f, y - 5 * s);
  ctx.stroke();

  // Seat
  ctx.fillStyle = "#2a2a2a";
  ctx.beginPath();
  ctx.ellipse(x - 1 * s * f, y - 4 * s, 2 * s, 1 * s, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ─── Wheel Helper ───────────────────────────────────────────────────────────

function drawWheel(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  radius: number
): void {
  // Outer rim
  ctx.fillStyle = "#3a3a3a";
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  // Inner hub
  ctx.fillStyle = "#5a4a3a";
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.3, 0, Math.PI * 2);
  ctx.fill();

  // Spokes
  ctx.strokeStyle = "#4a3a2a";
  ctx.lineWidth = radius * 0.12;
  const spokeCount = 6;
  for (let i = 0; i < spokeCount; i++) {
    const angle = (i / spokeCount) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle) * radius * 0.25, cy + Math.sin(angle) * radius * 0.25);
    ctx.lineTo(cx + Math.cos(angle) * radius * 0.85, cy + Math.sin(angle) * radius * 0.85);
    ctx.stroke();
  }

  // Rim highlight
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = radius * 0.08;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.9, -0.3, 0.8);
  ctx.stroke();
}

// ─── Color Utility ──────────────────────────────────────────────────────────

function darken(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = 1 - amount;
  return `#${Math.round(r * f).toString(16).padStart(2, "0")}${Math.round(g * f).toString(16).padStart(2, "0")}${Math.round(b * f).toString(16).padStart(2, "0")}`;
}
